import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { finalize } from "rxjs/operators";
import { Dialog } from "primeng/dialog";
import { ButtonDirective } from "primeng/button";
import { Chip } from "primeng/chip";
import { Message } from "primeng/message";

import { Client } from "../../client/client.model";
import { clientType } from "../../client/client-type.const";
import { ClientContactPersonRepository } from "../../client/client-contact-person-list/client-contact-person.repository";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { FormatContainerLabelPipe } from "../../shared/container/format-container-label.pipe";
import { ClientSelectComponent } from "../../ui/dropdown/client-select/client-select.component";
import { UploadComponent } from "../../ui/upload/upload.component";
import { WysiwygComponent } from "../../ui/wysiwyg/wysiwyg.component";
import { placeholder } from "../../ui/wysiwyg/config/placeholder.const";
import { AgentRequestRepository } from "../agent-request.repository";
import {
  AgentRequest,
  AgentRequestTarget,
  agentRequestType,
  NewAgentRequest,
} from "../models/agent-request.model";
import { BaseQuote } from "../models/quote-base.model";
import { BaseLclQuote } from "../models/quote-base-lcl.model";
import { FclQuote } from "../models/quote-fcl.model";

interface AgentRequestDialogMode {
  label: string;
  subject: string;
  question: string;
  closing: string;
}

// Rates and Charges share the popup; only these differ.
const modes: Record<"rates" | "charges", AgentRequestDialogMode> = {
  rates: {
    label: "Rates",
    subject: "Request for rates",
    question:
      "Could you please provide your best freight rate for the following shipment?",
    closing:
      "Please include all applicable freight surcharges and advise on the rate validity.",
  },
  charges: {
    label: "Charges",
    subject: "Request for local charges",
    question:
      "Could you please quote local charges for the following shipment?",
    closing: "Please include all applicable charges.",
  },
};

// The backend resolves only these for an agent; anything about the quote's customer is dropped.
const agentPlaceholders: readonly string[] = [
  placeholder.clientGreetings,
  placeholder.clientName,
  placeholder.quoteNumber,
  placeholder.myCompanyName,
  placeholder.myEmail,
  placeholder.mySignature,
];

const nonBreakingSpace = new RegExp(String.fromCharCode(0xa0), "g");

// Location and inland names are free text inserted into the HTML message.
const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

@Component({
  selector: "app-agent-request-dialog",
  templateUrl: "./agent-request-dialog.component.html",
  styleUrls: ["./agent-request-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Dialog,
    ButtonDirective,
    Chip,
    Message,
    ClientSelectComponent,
    UploadComponent,
    WysiwygComponent,
  ],
})
export class AgentRequestDialogComponent implements OnInit {
  private agentRequestRepository = inject(AgentRequestRepository);
  private contactPersonRepository = inject(ClientContactPersonRepository);
  private notifyService = inject(NotifyService);

  public readonly quote = input.required<BaseQuote>();
  public readonly target = input.required<AgentRequestTarget>();
  public readonly sent = output<AgentRequest[]>();
  public readonly closed = output<void>();

  public readonly agentType = clientType.Agent;
  public readonly agentPlaceholders = agentPlaceholders;
  public readonly mode = computed(() =>
    this.target().type === agentRequestType.freightRate
      ? modes.rates
      : modes.charges,
  );
  public readonly route = computed(() =>
    [this.target().originName, this.target().destinationName]
      .filter(Boolean)
      .join(" → "),
  );
  public readonly shipment = computed(() => this.describeShipment());

  public readonly agents = signal<Client[]>([]);
  public readonly subject = signal("");
  public readonly message = signal("");
  public readonly attachments = signal<string[]>([]);
  public readonly sending = signal(false);
  // Agent id → whether any of its contact persons has an email; absent while loading.
  private readonly agentHasEmail = signal<ReadonlyMap<number, boolean>>(
    new Map(),
  );

  public readonly agentsWithoutEmail = computed(() =>
    this.agents().filter(
      (agent) => this.agentHasEmail().get(agent.getId()) === false,
    ),
  );
  public readonly agentsWithoutEmailNames = computed(() =>
    this.agentsWithoutEmail()
      .map((agent) => agent.getName())
      .join(", "),
  );
  public readonly canSend = computed(
    () =>
      this.agents().length > 0 &&
      this.agents().every((agent) => this.agentHasEmail().get(agent.getId())) &&
      this.subject().trim() !== "" &&
      this.message().trim() !== "" &&
      !this.sending(),
  );

  public readonly defaultSubject = computed(() =>
    [this.mode().subject, this.shipment(), this.route()]
      .filter(Boolean)
      .join(" "),
  );
  // One paragraph per line of the template; a line with nothing to say is left out.
  public readonly defaultMessage = computed(() =>
    [
      `[[${placeholder.clientGreetings}]],`,
      this.mode().question,
      this.placeLine(),
      this.shipmentDetailsLine(),
      this.mode().closing,
      this.inlandHaulageLine(),
      "Thank you in advance!",
      `[[${placeholder.mySignature}]]`,
    ]
      .filter(Boolean)
      .join("<br/><br/>"),
  );

  ngOnInit(): void {
    this.subject.set(this.defaultSubject());
    this.message.set(this.defaultMessage());
  }

  public onAgentsChange(agents: Client[]): void {
    this.agents.set([...agents]);
    agents
      .filter((agent) => !this.agentHasEmail().has(agent.getId()))
      .forEach((agent) => this.checkAgentEmail(agent));
  }

  public onSubjectChange(subject: string): void {
    this.subject.set(this.toPlainText(subject));
  }

  public onMessageChange(message: string): void {
    this.message.set(message);
  }

  public addAttachment(attachment: string): void {
    this.attachments.update((attachments) => [...attachments, attachment]);
  }

  public removeAttachment(attachment: string): void {
    this.attachments.update((attachments) =>
      attachments.filter((item) => item !== attachment),
    );
  }

  public fileName(url: string): string {
    return url.split("/").pop();
  }

  public send(): void {
    if (!this.canSend()) {
      return;
    }

    this.sending.set(true);
    this.agentRequestRepository
      .create(this.quote().getId(), this.newRequest())
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe({
        next: (requests) => {
          this.notifyService.toast(
            TOAST.SUCCESS,
            `${requests.length} ${requests.length === 1 ? "request" : "requests"} sent`,
          );
          this.sent.emit(requests);
        },
        error: (err) =>
          this.notifyService.toast(
            TOAST.ERROR,
            "Request has not been sent",
            <string>new BackendErrorPipe().transform(err),
          ),
      });
  }

  public close(): void {
    this.closed.emit();
  }

  private checkAgentEmail(agent: Client): void {
    this.contactPersonRepository.list(agent).subscribe({
      next: (persons) =>
        this.markAgentEmail(
          agent,
          persons.some((person) => !!person.getEmail()),
        ),
      error: () => this.markAgentEmail(agent, false),
    });
  }

  private markAgentEmail(agent: Client, hasEmail: boolean): void {
    this.agentHasEmail.update((known) =>
      new Map(known).set(agent.getId(), hasEmail),
    );
  }

  private newRequest(): NewAgentRequest {
    const target = this.target();

    return {
      type: target.type,
      agentIds: this.agents().map((agent) => agent.getId()),
      originLegId: target.originLegId,
      destinationLegId: target.destinationLegId,
      subject: this.subject(),
      message: this.message(),
      attachments: this.attachments(),
    };
  }

  private isRatesRequest(): boolean {
    return this.target().type === agentRequestType.freightRate;
  }

  // A charges request names the one location it is for.
  private locationName(): string | null {
    return this.target().originName ?? this.target().destinationName;
  }

  private placeLine(): string | null {
    if (this.isRatesRequest()) {
      return this.route()
        ? `<strong>Route:</strong> ${escapeHtml(this.route())}`
        : null;
    }

    return this.locationName()
      ? `<strong>Location:</strong> ${escapeHtml(this.locationName())}`
      : null;
  }

  private shipmentDetailsLine(): string | null {
    const details = this.describeShipmentDetails();

    return details
      ? `<strong>Shipment details:</strong><br/>${escapeHtml(details)}`
      : null;
  }

  // Origin haulage runs to the location, destination haulage away from it.
  private inlandHaulageLine(): string | null {
    const { type, inlandHaulage } = this.target();
    const location = this.locationName();
    if (this.isRatesRequest() || !inlandHaulage || !location) {
      return null;
    }
    const [from, to] =
      type === agentRequestType.originCharges
        ? [inlandHaulage, location]
        : [location, inlandHaulage];

    return `Please also include inland haulage from ${escapeHtml(from)} to ${escapeHtml(to)}.`;
  }

  // The subject's shipment plus pieces; a rates request also says how the cargo is handled.
  private describeShipmentDetails(): string {
    const quote = this.quote();
    if (!(quote instanceof BaseLclQuote)) {
      return this.shipment();
    }
    const cargo = [
      this.shipment(),
      quote.getPieces() ? `${quote.getPieces()} PCS` : null,
    ]
      .filter(Boolean)
      .join(" ");
    const handling = this.isRatesRequest()
      ? [this.describeStackability(quote), this.describePallets(quote)]
      : [];

    return [cargo, ...handling].filter(Boolean).join(", ");
  }

  // Cargo is stackable only when every package is.
  private describeStackability(quote: BaseLclQuote): string | null {
    const packages = quote.getPackages() ?? [];
    if (packages.length === 0) {
      return null;
    }

    return packages.every((item) => item.stackable)
      ? "stackable"
      : "non-stackable";
  }

  private describePallets(quote: BaseLclQuote): string | null {
    const pallets = (quote.getPackages() ?? [])
      .filter((item) => item.pallet)
      .reduce((total, item) => total + (item.quantity || 0), 0);
    if (pallets === 0) {
      return null;
    }

    return `${pallets} ${pallets === 1 ? "pallet" : "pallets"}`;
  }

  // Unit and shipment parameters only: the quote type stays out of what agents read.
  private describeShipment(): string {
    const quote = this.quote();
    if (quote instanceof FclQuote) {
      const pipe = new FormatContainerLabelPipe();

      return (quote.getEquipment() ?? [])
        .map((container) => pipe.transform(container))
        .join(", ");
    }
    if (quote instanceof BaseLclQuote) {
      return [
        quote.getWeight() ? `${quote.getWeight()}kg` : null,
        quote.getMeasurement() ? `${quote.getMeasurement()}CBM` : null,
      ]
        .filter(Boolean)
        .join(" ");
    }

    return "";
  }

  private toPlainText(html: string): string {
    const container = document.createElement("div");
    container.innerHTML = html;

    return (container.textContent ?? "").replace(nonBreakingSpace, " ").trim();
  }
}
